import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMySets } from "@/lib/queries";
import { VISIBILITIES } from "@/lib/mtg";
import { EmptyState, PageHeader } from "@/components/common/PageParts";
import { VisibilityBadge } from "@/components/common/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export function SetCard({ set, showOwner = true }) {
  return (
    <Link to={`/sets/${set.set_id}`} className="panel group block p-6 transition-[border-color,box-shadow] duration-300 hover:border-amber-600/60 hover:shadow-[0_0_20px_rgba(217,119,6,0.15)]" data-testid={`set-card-${set.set_id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="rounded border border-amber-700/50 bg-amber-500/10 px-2 py-1 font-mono text-xs font-semibold text-amber-300">{set.code}</span>
        {set.visibility !== "public" && <VisibilityBadge visibility={set.visibility} />}
      </div>
      <h3 className="mt-4 font-heading text-lg text-amber-100 group-hover:text-amber-200">{set.name}</h3>
      {set.description && <p className="mt-2 line-clamp-2 text-sm text-slate-400">{set.description}</p>}
      <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">
        {set.card_count} cards{showOwner && set.owner ? ` · @${set.owner.username}` : ""}
      </p>
    </Link>
  );
}

export function CreateSetDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", description: "", visibility: "public" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sets", form);
      toast.success("Set created");
      qc.invalidateQueries({ queryKey: ["me", "sets"] });
      qc.invalidateQueries({ queryKey: ["sets"] });
      setOpen(false);
      setForm({ code: "", name: "", description: "", visibility: "public" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-gold" data-testid="create-set-button"><Plus className="h-4 w-4" />New set</button></DialogTrigger>
      <DialogContent className="border-[#2A3447] bg-[#121620] text-slate-200" data-testid="create-set-dialog">
        <DialogHeader><DialogTitle className="font-heading text-amber-100">Create a custom set</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="field-label" htmlFor="set-code">Code</label>
              <input id="set-code" required pattern="[A-Za-z0-9]{2,8}" maxLength={8} className="field uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="set-code-input" />
            </div>
            <div className="col-span-2">
              <label className="field-label" htmlFor="set-name">Name</label>
              <input id="set-name" required className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="set-name-input" />
            </div>
          </div>
          <div>
            <label className="field-label" htmlFor="set-desc">Description</label>
            <textarea id="set-desc" rows={3} className="field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="set-description-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="set-vis">Visibility</label>
            <select id="set-vis" className="field capitalize" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="set-visibility-select">
              {VISIBILITIES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <button type="submit" className="btn-gold w-full" disabled={busy} data-testid="set-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Create set</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Sets() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["sets", "public", "list"], queryFn: () => api.get("/sets", { params: { page_size: 100 } }).then((r) => r.data) });
  const { data: mine = [] } = useMySets();
  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Custom expansions" title="Sets" testid="sets-header"
        description="Creator-made expansions, cubes and collections. Cards can belong to one set each."
        actions={user && <CreateSetDialog />} />
      {user && mine.length > 0 && (
        <section className="mb-14" data-testid="my-sets-section">
          <h2 className="mb-5 font-heading text-xl text-amber-200/90">Your sets</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{mine.map((s) => <SetCard key={s.set_id} set={s} showOwner={false} />)}</div>
        </section>
      )}
      <h2 className="mb-5 font-heading text-xl text-amber-200/90">Community sets</h2>
      {isLoading ? null : data?.items?.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="public-sets-grid">{data.items.map((s) => <SetCard key={s.set_id} set={s} />)}</div>
      ) : (
        <EmptyState icon={BookOpen} title="No public sets yet" testid="sets-empty" />
      )}
    </div>
  );
}
